import { useState } from "react";
import { Link, router } from "@inertiajs/react";
import { CheckCheck, ChevronLeft, ExternalLink, Eye, EyeOff, Link2, Send, Shield, Sparkles, Trash2, User, Copy } from "lucide-react";
import AppLayout from "../../layouts/AppLayout";
import { Button } from "../../components/ui/button";

interface Campaign {
  id: number;
  name: string;
  source_type: "job_alert" | "manual_upload" | "skm";
  source_name: string | null;
  source_href: string | null;
  status: string;
  total_count: number;
  cleared_count: number;
  blocked_count: number;
  tentative_count: number;
  sent_count: number;
  ai_drafted: boolean;
  tone: string;
  subject_template: string;
  body_template: string;
  created_at: string;
  approved_at: string | null;
  sent_at: string | null;
}

interface Draft {
  id: number;
  recipient_email: string;
  recipient_name: string;
  recipient_firm: string | null;
  subject: string;
  body: string;
  dnc_status: string;
  dnc_reason: string | null;
  override_reason: string | null;
  included_in_send: boolean;
  status: string;
  sent_at: string | null;
  dedup_status: string;
  dedup_reason: string | null;
  dedup_window_days: number | null;
  linkedin_url: string | null;
  leopard_link: string | null;
  profile_url: string | null;
}

interface Props {
  campaign: Campaign;
  drafts: Draft[];
  ai_assist: boolean;
}

const SOURCE_CONFIG = {
  job_alert:     { label: "Job Alert",     className: "bg-green-50 text-green-700 border-green-200" },
  manual_upload: { label: "Manual Upload", className: "bg-blue-50 text-blue-700 border-blue-200" },
  skm:           { label: "SKM",           className: "bg-purple-50 text-purple-700 border-purple-200" },
};

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  pending_dnc:  { label: "Pending DNC",  className: "bg-gray-100 text-gray-500 border-gray-200" },
  dnc_complete: { label: "DNC Done",     className: "bg-yellow-50 text-yellow-700 border-yellow-200" },
  drafting:     { label: "Drafting",     className: "bg-blue-50 text-blue-700 border-blue-200" },
  draft_review: { label: "Draft Review", className: "bg-amber-50 text-amber-700 border-amber-200" },
  approved:     { label: "Approved",     className: "bg-green-50 text-green-700 border-green-200" },
  sending:      { label: "Sending",      className: "bg-blue-50 text-blue-600 border-blue-200" },
  sent:         { label: "Sent",         className: "bg-gray-100 text-gray-500 border-gray-200" },
};

const DNC_CONFIG: Record<string, { label: string; className: string }> = {
  pending:             { label: "Pending",    className: "bg-gray-100 text-gray-500" },
  clear:               { label: "Clear",      className: "bg-green-50 text-green-700" },
  blocked:             { label: "Blocked",    className: "bg-red-50 text-red-700" },
  tentatively_blocked: { label: "Tentative",  className: "bg-amber-50 text-amber-700" },
  overridden:          { label: "Overridden", className: "bg-purple-50 text-purple-700" },
};

const DRAFT_STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  draft:    { label: "Draft",    className: "bg-gray-100 text-gray-500" },
  approved: { label: "Approved", className: "bg-blue-50 text-blue-700" },
  sent:     { label: "Sent",     className: "bg-green-50 text-green-700" },
  failed:   { label: "Failed",   className: "bg-red-50 text-red-700" },
};

function relativeDate(iso: string | null): string | null {
  if (!iso) return null;
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / 86_400_000);
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function CampaignHeader({ campaign }: { campaign: Campaign }) {
  const src = SOURCE_CONFIG[campaign.source_type] ?? SOURCE_CONFIG.manual_upload;
  const st  = STATUS_CONFIG[campaign.status] ?? { label: campaign.status, className: "bg-gray-100 text-gray-500 border-gray-200" };

  return (
    <div className="mb-4">
      <Link href="/email-campaigns" className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-green-700 mb-1">
        <ChevronLeft className="w-3 h-3" /> Email Campaigns
      </Link>
      <h1 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
        {campaign.name}
        {campaign.ai_drafted && (
          <span className="text-xs text-purple-600 bg-purple-50 border border-purple-200 px-1.5 py-0.5 rounded">AI</span>
        )}
      </h1>
      <div className="flex items-center gap-2 mt-1">
        <span className={`text-xs font-medium px-2 py-0.5 rounded border ${src.className}`}>{src.label}</span>
        <span className={`text-xs font-medium px-2 py-0.5 rounded border ${st.className}`}>{st.label}</span>
        {campaign.source_name && campaign.source_href && (
          <Link href={campaign.source_href} className="text-xs text-gray-400 hover:text-green-700">
            {campaign.source_name}
          </Link>
        )}
      </div>
    </div>
  );
}

function StatsBar({ campaign }: { campaign: Campaign }) {
  const stats = [
    { value: campaign.total_count,    label: "Total",     color: "text-gray-800" },
    { value: campaign.cleared_count,  label: "Clear",     color: "text-green-700" },
    ...(campaign.blocked_count   > 0 ? [{ value: campaign.blocked_count,   label: "Blocked",   color: "text-red-600" }]   : []),
    ...(campaign.tentative_count > 0 ? [{ value: campaign.tentative_count, label: "Tentative", color: "text-amber-600" }] : []),
    ...(campaign.sent_count      > 0 ? [{ value: campaign.sent_count,      label: "Sent",      color: "text-gray-600" }]  : []),
  ];

  return (
    <div className="flex items-center gap-4 bg-white rounded-lg border border-gray-200 px-4 py-3 mb-6">
      {stats.map((s, i) => (
        <div key={s.label} className="flex items-center gap-4">
          {i > 0 && <div className="w-px h-8 bg-gray-100" />}
          <div className="text-center">
            <div className={`font-semibold text-sm ${s.color}`}>{s.value}</div>
            <div className="text-xs text-gray-400">{s.label}</div>
          </div>
        </div>
      ))}
      <div className="flex-1" />
      <div className="text-xs text-gray-400">
        Created {relativeDate(campaign.created_at)}
        {campaign.approved_at && <span className="ml-3">Approved {relativeDate(campaign.approved_at)}</span>}
        {campaign.sent_at && <span className="ml-3">Sent {relativeDate(campaign.sent_at)}</span>}
      </div>
    </div>
  );
}

function ActionBar({ campaign }: { campaign: Campaign }) {
  const isSending = campaign.status === "sending";

  function approve() {
    router.patch(`/email-campaigns/${campaign.id}/approve`);
  }

  function sendTest() {
    router.post(`/email-campaigns/${campaign.id}/send_test`);
  }

  function sendAll() {
    if (!confirm(`Send to ${campaign.cleared_count} recipients? This cannot be undone.`)) return;
    router.post(`/email-campaigns/${campaign.id}/send`);
  }

  function deleteCampaign() {
    if (!confirm("Delete this campaign? This cannot be undone.")) return;
    router.delete(`/email-campaigns/${campaign.id}`);
  }

  return (
    <div className="flex items-center gap-2 mb-6">
      {campaign.status === "draft_review" && (
        <button
          onClick={approve}
          className="flex items-center gap-1.5 text-sm bg-green-700 text-white px-3 py-1.5 rounded-md hover:bg-green-800 transition-colors"
        >
          <CheckCheck className="w-4 h-4" /> Approve
        </button>
      )}
      {(campaign.status === "draft_review" || campaign.status === "approved") && (
        <button
          onClick={sendTest}
          className="flex items-center gap-1.5 text-sm border border-gray-200 text-gray-600 px-3 py-1.5 rounded-md hover:bg-gray-50 transition-colors"
        >
          <Eye className="w-4 h-4" /> Send Test
        </button>
      )}
      {campaign.status === "approved" && (
        <button
          onClick={sendAll}
          className="flex items-center gap-1.5 text-sm bg-blue-700 text-white px-3 py-1.5 rounded-md hover:bg-blue-800 transition-colors"
        >
          <Send className="w-4 h-4" /> Send All ({campaign.cleared_count})
        </button>
      )}
      {isSending && (
        <span className="text-sm text-blue-600 italic">Sending in progress...</span>
      )}
      <div className="flex-1" />
      <button
        onClick={deleteCampaign}
        className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-red-500 border border-gray-200 hover:border-red-200 px-3 py-1.5 rounded-md transition-colors"
      >
        <Trash2 className="w-4 h-4" /> Delete
      </button>
    </div>
  );
}

function TemplateEditor({ campaign, aiAssist }: { campaign: Campaign; aiAssist: boolean }) {
  const [tpl, setTpl] = useState({
    subject: campaign.subject_template,
    body:    campaign.body_template,
    tone:    campaign.tone,
  });
  const [dirty, setDirty]         = useState(false);
  const [redrafting, setRedrafting] = useState(false);
  const isSent = campaign.status === "sending" || campaign.status === "sent";
  const canRedraft = aiAssist && campaign.source_type === "job_alert" && !isSent;

  function update(field: keyof typeof tpl, value: string) {
    setTpl(prev => ({ ...prev, [field]: value }));
    setDirty(true);
  }

  function save() {
    router.patch(`/email-campaigns/${campaign.id}`, { campaign: tpl });
    setDirty(false);
  }

  function redraft() {
    if (!confirm(`Re-draft all ${campaign.total_count} emails with AI using ${tpl.tone} tone? This will overwrite existing drafts.`)) return;
    setRedrafting(true);
    router.post(`/email-campaigns/${campaign.id}/redraft`, { tone: tpl.tone }, {
      onFinish: () => setRedrafting(false),
    });
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 mb-6">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <h2 className="text-sm font-medium text-gray-700">Email Template</h2>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <label className="text-xs text-gray-500">Tone:</label>
            <select
              value={tpl.tone}
              onChange={(e) => update("tone", e.target.value)}
              disabled={isSent || !aiAssist}
              title={!aiAssist ? "Requires AI Assist (enable in Settings)" : undefined}
              className="text-xs border border-gray-200 rounded px-2 py-1 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="conservative">Conservative</option>
              <option value="assertive">Assertive</option>
            </select>
            {!aiAssist && <span className="text-xs text-gray-400 italic">AI only</span>}
          </div>
          {dirty && !isSent && (
            <button
              onClick={save}
              className="text-xs bg-green-700 text-white px-2.5 py-1 rounded hover:bg-green-800 transition-colors"
            >
              Save
            </button>
          )}
          {canRedraft && (
            <button
              onClick={redraft}
              disabled={redrafting}
              className="flex items-center gap-1 text-xs text-purple-700 border border-purple-200 px-2.5 py-1 rounded hover:bg-purple-50 transition-colors disabled:opacity-50"
            >
              <Sparkles className="w-3 h-3" />
              {redrafting ? "Queuing..." : "Re-draft with AI"}
            </button>
          )}
        </div>
      </div>
      <div className="p-4 space-y-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Subject</label>
          <input
            type="text"
            value={tpl.subject}
            onChange={(e) => update("subject", e.target.value)}
            disabled={isSent}
            className="w-full text-sm border border-gray-200 rounded px-3 py-2 text-gray-800 font-mono focus:outline-none focus:ring-1 focus:ring-green-500 disabled:bg-gray-50 disabled:opacity-70"
            placeholder="Subject line — use {{first_name}}, {{firm_name}} tokens"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Body</label>
          <textarea
            value={tpl.body}
            onChange={(e) => update("body", e.target.value)}
            disabled={isSent}
            rows={12}
            className="w-full text-sm border border-gray-200 rounded px-3 py-2 text-gray-800 font-mono focus:outline-none focus:ring-1 focus:ring-green-500 disabled:bg-gray-50 disabled:opacity-70 resize-y"
            placeholder="Email body — use {{first_name}}, {{last_name}}, {{firm_name}}, {{practice_area}}, {{location}}, {{jd_year}}"
          />
        </div>
        <p className="text-xs text-gray-400">
          Tokens:{" "}
          {["first_name", "last_name", "firm_name", "practice_area", "location", "jd_year"].map((t) => (
            <code key={t} className="bg-gray-100 px-1 rounded mr-1">{`{{${t}}}`}</code>
          ))}
        </p>
      </div>
    </div>
  );
}

function DraftRow({
  draft,
  campaignId,
  expanded,
  onToggle,
}: {
  draft: Draft;
  campaignId: number;
  expanded: boolean;
  onToggle: () => void;
}) {
  const [showOverride, setShowOverride] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");

  const dnc = DNC_CONFIG[draft.dnc_status]          ?? { label: draft.dnc_status, className: "bg-gray-100 text-gray-500" };
  const ds  = DRAFT_STATUS_CONFIG[draft.status]     ?? { label: draft.status,     className: "bg-gray-100 text-gray-500" };
  const canOverride = draft.dnc_status === "blocked" || draft.dnc_status === "tentatively_blocked";

  function submitOverride() {
    if (!overrideReason.trim()) return;
    router.patch(`/email-campaigns/${campaignId}/drafts/${draft.id}/override`, { override_reason: overrideReason });
    setShowOverride(false);
    setOverrideReason("");
  }

  return (
    <>
      <tr className="hover:bg-gray-50 transition-colors">
        <td className="px-4 py-3">
          <div className="font-medium text-sm text-gray-800 flex items-center gap-1.5">
            {draft.recipient_name}
            {draft.linkedin_url && (
              <a href={draft.linkedin_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:text-blue-800" title="LinkedIn">
                <Link2 className="w-3.5 h-3.5" />
              </a>
            )}
            {draft.leopard_link && (
              <a href={draft.leopard_link} target="_blank" rel="noreferrer" className="text-amber-600 hover:text-amber-800" title="Leopard">
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            )}
            {draft.profile_url && (
              <a href={draft.profile_url} target="_blank" rel="noreferrer" className="text-gray-400 hover:text-gray-700" title="Profile">
                <User className="w-3.5 h-3.5" />
              </a>
            )}
          </div>
          {draft.recipient_firm && <div className="text-xs text-gray-400">{draft.recipient_firm}</div>}
        </td>
        <td className="px-4 py-3 text-xs text-gray-500">{draft.recipient_email}</td>
        <td className="px-4 py-3">
          <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${dnc.className}`}>{dnc.label}</span>
          {draft.dnc_reason && (
            <div className="text-xs text-gray-400 mt-0.5 max-w-[180px] truncate" title={draft.dnc_reason}>
              {draft.dnc_reason}
            </div>
          )}
        </td>
        <td className="px-4 py-3">
          <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${ds.className}`}>{ds.label}</span>
          {draft.sent_at && <div className="text-xs text-gray-400 mt-0.5">{relativeDate(draft.sent_at)}</div>}
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-1.5">
            <button
              onClick={onToggle}
              className="text-xs text-gray-400 hover:text-gray-700 border border-gray-200 rounded px-2 py-1 flex items-center gap-1"
            >
              {expanded ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
              {expanded ? "Hide" : "Preview"}
            </button>
            {canOverride && (
              <button
                onClick={() => setShowOverride((v) => !v)}
                className="text-xs text-amber-600 hover:text-amber-700 border border-amber-200 rounded px-2 py-1 flex items-center gap-1"
              >
                <Shield className="w-3 h-3" /> Override
              </button>
            )}
            {draft.override_reason && (
              <span
                className="text-xs text-purple-600 italic truncate max-w-[120px]"
                title={draft.override_reason}
              >
                {draft.override_reason}
              </span>
            )}
          </div>
        </td>
      </tr>

      {showOverride && (
        <tr>
          <td colSpan={5} className="px-4 pb-3 bg-amber-50">
            <div className="flex items-center gap-2 p-2">
              <input
                type="text"
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
                placeholder="Override reason (required)"
                className="flex-1 text-xs border border-amber-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-amber-400"
              />
              <button
                onClick={submitOverride}
                disabled={!overrideReason.trim()}
                className="text-xs bg-amber-600 text-white rounded px-3 py-1.5 hover:bg-amber-700 disabled:opacity-50 transition-colors"
              >
                Confirm Override
              </button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setShowOverride(false); setOverrideReason(""); }}
              >
                Cancel
              </Button>
            </div>
          </td>
        </tr>
      )}

      {expanded && (
        <tr>
          <td colSpan={5} className="px-4 pb-4">
            <div className="bg-gray-50 border border-gray-200 rounded p-3">
              <div className="text-xs text-gray-500 mb-1.5">
                <span className="font-medium">Subject:</span> {draft.subject}
              </div>
              <pre className="text-xs text-gray-700 whitespace-pre-wrap font-sans leading-relaxed">{draft.body}</pre>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function DraftsTable({ drafts, campaignId }: { drafts: Draft[]; campaignId: number }) {
  const [expanded, setExpanded] = useState<number | null>(null);

  const sendable = drafts.filter((d) => d.dedup_status === "unique");

  function toggleExpand(id: number) {
    setExpanded((prev) => (prev === id ? null : id));
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100">
        <h2 className="text-sm font-medium text-gray-700">Recipients ({sendable.length})</h2>
      </div>
      {sendable.length === 0 ? (
        <div className="py-12 text-center text-sm text-gray-400">No drafts in this campaign.</div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50">
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Recipient</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Email</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">DNC</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Status</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {sendable.map((d) => (
              <DraftRow
                key={d.id}
                draft={d}
                campaignId={campaignId}
                expanded={expanded === d.id}
                onToggle={() => toggleExpand(d.id)}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function DedupBlockedPane({ drafts, campaignId }: { drafts: Draft[]; campaignId: number }) {
  const [open, setOpen] = useState(false);
  const blocked = drafts.filter((d) => d.dedup_status !== "unique");

  if (blocked.length === 0) return null;

  function includeAnyway(draftId: number) {
    router.patch(`/email-campaigns/${campaignId}/drafts/${draftId}/override_dedup`);
  }

  return (
    <div className="mt-4 bg-white rounded-lg border border-gray-200 overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
      >
        <span className="text-sm font-medium text-gray-700 flex items-center gap-2">
          <Copy className="w-4 h-4 text-gray-400" />
          Dedup-blocked ({blocked.length})
        </span>
        <span className="text-xs text-gray-400">{open ? "Hide" : "Show"}</span>
      </button>

      {open && (
        <div className="border-t border-gray-100">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Recipient</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Email</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Reason</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {blocked.map((d) => (
                <tr key={d.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="font-medium text-sm text-gray-800">{d.recipient_name}</div>
                    {d.recipient_firm && <div className="text-xs text-gray-400">{d.recipient_firm}</div>}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">{d.recipient_email}</td>
                  <td className="px-4 py-3">
                    <span className="text-xs px-1.5 py-0.5 rounded font-medium bg-orange-50 text-orange-700">
                      {d.dedup_status === "duplicate_recent_send"
                        ? "Recent send"
                        : d.dedup_status === "duplicate_placed"
                        ? "Recently placed"
                        : "Active draft"}
                    </span>
                    {d.dedup_reason && (
                      <div className="text-xs text-gray-400 mt-0.5">{d.dedup_reason}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => includeAnyway(d.id)}
                      className="text-xs text-gray-500 hover:text-green-700 border border-gray-200 hover:border-green-300 rounded px-2 py-1 transition-colors"
                    >
                      Include anyway
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function EmailCampaignsShow({ campaign, drafts, ai_assist }: Props) {
  return (
    <AppLayout title={campaign.name}>
      <CampaignHeader campaign={campaign} />
      <StatsBar campaign={campaign} />
      <ActionBar campaign={campaign} />
      <TemplateEditor campaign={campaign} aiAssist={ai_assist} />
      <DraftsTable drafts={drafts} campaignId={campaign.id} />
      <DedupBlockedPane drafts={drafts} campaignId={campaign.id} />
    </AppLayout>
  );
}
